import React from "react";
import {
  Container,
  Typography,
  Box,
  useTheme,
  alpha,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import FeatureLayout from "./FeatureLayout";

// App screenshot placeholders
import schedulingScreenshot1 from "../../../images/features/scheduling-1.png";
import schedulingScreenshot2 from "../../../images/features/scheduling-2.png";
import schedulingScreenshot3 from "../../../images/features/scheduling-3.png";

const SmartScheduling = () => {
  const theme = useTheme();

  const benefits = [
    {
      icon: <SyncAltIcon />,
      title: "Calendar Sync",
      description: "Automatically syncs with Airbnb, Vrbo, and Booking.com. No manual entry required.",
    },
    {
      icon: <NotificationsActiveIcon />,
      title: "Instant Notifications",
      description: "Get alerted the moment a guest checks out. Book cleanings immediately.",
    },
    {
      icon: <CalendarTodayIcon />,
      title: "Recurring Cleanings",
      description: "Set up weekly, bi-weekly, or monthly schedules for regular maintenance.",
    },
    {
      icon: <AutoAwesomeIcon />,
      title: "Smart Assignment",
      description: "Automatically assign cleaners based on availability, ratings, and preferences.",
    },
    {
      icon: <CheckCircleIcon />,
      title: "Conflict Detection",
      description: "Never double-book. Our system prevents scheduling conflicts automatically.",
    },
    {
      icon: <ScheduleIcon />,
      title: "Flexible Windows",
      description: "Set preferred cleaning windows (morning, afternoon, or anytime).",
    },
  ];

  const stats = [
    { value: "99.9%", label: "Uptime guarantee" },
    { value: "10k+", label: "Schedules/month" },
    { value: "15min", label: "Setup time" },
    { value: "3-way", label: "Integration" },
  ];

  return (
    <FeatureLayout
      title="Smart Scheduling"
      subtitle="Never manually coordinate a cleaning again"
      description="Connect your booking calendar once, and we'll automatically schedule cleanings after every guest checkout. Smart conflict detection ensures no double-booking. Your cleaners get notified instantly."
      icon={<ScheduleIcon sx={{ fontSize: 32 }} />}
      iconGradient="linear-gradient(135deg, #667eea, #764ba2)"
      breadcrumbs={[
        { label: "Features", link: "/features" },
        { label: "Smart Scheduling" },
      ]}
      benefits={benefits}
      stats={stats}
      imageRight={schedulingScreenshot1}
      imageLeft={schedulingScreenshot2}
    >
      {/* How Auto-Scheduling Works */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            mb: 4,
            textAlign: "center",
            fontSize: { xs: "1.8rem", md: "2.5rem" },
          }}
        >
          How Auto-Scheduling Works
        </Typography>
        
        <Grid container spacing={4}>
          {[
            { step: "1", title: "Connect Calendar", desc: "Link your Airbnb/Vrbo calendar - takes 30 seconds" },
            { step: "2", title: "Set Preferences", desc: "Choose cleaning windows, preferred cleaners, and pricing" },
            { step: "3", title: "Automatic Triggers", desc: "Guest checks out → Cleaning is scheduled automatically" },
            { step: "4", title: "Cleaner Confirms", desc: "Cleaner accepts instantly. You get notified", },
          ].map((item, idx) => (
            <Grid item xs={12} sm={6} md={3} key={idx}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  textAlign: "center",
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "16px",
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "1.5rem",
                    fontWeight: "bold",
                    mx: "auto",
                    mb: 2,
                  }}
                >
                  {item.step}
                </Box>
                <Typography variant="h6" fontWeight="700" gutterBottom>
                  {item.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.desc}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Integration Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h4" fontWeight="800" gutterBottom>
              Works With Your Favorite Platforms
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
              FreshSweeper seamlessly integrates with all major booking platforms. 
              One connection, automatic syncing, zero manual work.
            </Typography>
            <List>
              {[
                "Airbnb - Two-way calendar sync",
                "Vrbo - Automatic checkout detection",
                "Booking.com - Instant scheduling",
                "Google Calendar - Export support",
              ].map((text, idx) => (
                <ListItem key={idx}>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: theme.palette.success.main }} />
                  </ListItemIcon>
                  <ListItemText primary={text} />
                </ListItem>
              ))}
            </List>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src={schedulingScreenshot3}
              alt="Platform integration"
              sx={{
                width: "100%",
                borderRadius: 4,
                boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
              }}
            />
          </Grid>
        </Grid>
      </Container>
    </FeatureLayout>
  );
};

export default SmartScheduling;